/* eslint-disable @typescript-eslint/member-ordering */

import {
  Directive,
  DestroyRef,
  effect,
  EffectRef,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';

@Directive({
  selector: '[uiEqualHeightGroup]',
  exportAs: 'uiEqualHeightGroup',
})
export class EqualHeightGroupDirective {
  public readonly equalHeightEnabled: InputSignal<boolean> = input<boolean>(true);
  private readonly minHeightState: WritableSignal<number | null> = signal<number | null>(null);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly targets: Set<HTMLElement> = new Set<HTMLElement>();
  private resizeObserver?: ResizeObserver;

  private readonly enabledEffect: EffectRef = effect(() => {
    if (!this.equalHeightEnabled()) {
      this.minHeightState.set(null);

      return;
    }

    this.updateMinHeight();
  });

  public readonly minHeight: Signal<number | null> = this.minHeightState.asReadonly();

  public register(target: HTMLElement): void {
    if (this.targets.has(target)) {
      return;
    }

    this.targets.add(target);
    this.ensureResizeObserver();
    this.resizeObserver?.observe(target);
    this.updateMinHeight();
  }

  public unregister(target: HTMLElement): void {
    if (!this.targets.delete(target)) {
      return;
    }

    this.resizeObserver?.unobserve(target);
    this.updateMinHeight();
  }

  private ensureResizeObserver(): void {
    if (this.resizeObserver || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateMinHeight();
    });

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
    });
  }

  private updateMinHeight(): void {
    if (!this.equalHeightEnabled() || !this.targets.size) {
      this.minHeightState.set(null);

      return;
    }

    const maxHeight: number = Math.max(
      ...Array.from(this.targets, target => target.offsetHeight),
    );

    this.minHeightState.set(maxHeight);
  }
}
